import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import QuoteDetailHeader from './components/QuoteDetailHeader';
import useQuoteSectionCollapse from './components/useQuoteSectionCollapse';
import QuoteDetailTabs, { WAREHOUSE_TABS, DIRECT_TABS } from './components/QuoteDetailTabs';
import CustomerInfoTab from './tabs/CustomerInfoTab';
import DrayTab from './tabs/DrayTab';
import { warehouseWithTotals } from './quoteWarehouseTotals';
import WarehouseTab from './tabs/WarehouseTab';
import DirectTab from './tabs/DirectTab';
import DeliveryTab from './tabs/DeliveryTab';
import DocumentsTab from './tabs/DocumentsTab';
import NotesTab from './tabs/NotesTab';
import HistoryTab from './tabs/HistoryTab';
import { SAMPLE_QUOTE_DETAIL, type QuoteDetailData, type DirectFields, type WarehouseFields, type LocationLookupRow, type BillToLookupRow, type TariffRateRow, type StopoffRow } from './fixtures';
import {
  type ChargeOp, type StopoffOp,
  stageChargeAdd, stageChargeUpdate, stageChargeDelete,
  stageStopoffAdd, stageStopoffUpdate, stageStopoffDelete,
  replayChargeOps, replayStopoffOps,
  stopoffRowFromPayload, stopoffRowPatch,
} from './quoteChildStaging';
import { openQuotePdf } from '../../utils/quotePdf';
import PrintButton from '../../components/printing/PrintButton';
import quoteService, { type ChargeCatalogRow, type HandlingCatalogRow, type LaneFieldsPayload, type ProductRateFieldsPayload, type RateStandards, type RevisionCreatePayload, type StopoffFieldsPayload, type DupeCandidate, type DupeCheckParams, type FscTariff } from '../../services/quote.service';
import NewRevisionDialog from './components/NewRevisionDialog';
import SendQuoteDialog from './components/SendQuoteDialog';
import MigrationVerifyBadge from './components/MigrationVerifyBadge';
import useEstimatedRevenue from './components/useEstimatedRevenue';
import { drayGuard, transloadGuard, belowMinimum, minRateBlocksSave } from './rateGuards';
import { QUOTE_BYPASS_MINIMUM_RATE_GROUP_ID } from './quoteOptions';
import { useTryPermissions } from '../../contexts/PermissionContext';
import { useQuoteAcls } from '../../hooks/useQuoteAcls';
// Shared edit-host field maps + PATCH builder (also used by the grouped-display
// QuoteLaneRow — see quoteEditPatch.ts). Keeps DIRECT_MAP / patch / dirty / save
// logic in one place rather than duplicated per surface.
import { buildUpdatePayload, computeDirty, saveErrorMessage, todayDisplay } from './quoteEditPatch';
import { statusBadgeClass } from './quoteColors';
import { isQuoteSecured, isPastEndDate, isMoveTypeEditable } from './quoteFieldRules';
import { isBrokenCustomerTariff, isDeletedCustomerTariff, laneFsc } from '../../utils/fscTariff';
import ConfirmSecuredChangesModal from './components/ConfirmSecuredChangesModal';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/** V1 editrecord_quote.pl warns when a copied revision skips charges whose
 *  catalog row was deleted. The new-revision/dupe flow navigates away on
 *  success, so surface this as a blocking alert before the route changes. */
export function warnDroppedCharges(dropped: string[] | undefined): void {
  if (!dropped || dropped.length === 0) return;
  window.alert(
    'The following charges were on the prior revision but are no longer in ' +
      'the charge catalog, so they were NOT copied:\n\n' +
      dropped.map((d) => `  • ${d}`).join('\n'),
  );
}

interface Props {
  /** create = new quote draft (seeded from URL params), view = existing quote. */
  mode: 'create' | 'view';
}

/** Parse "43448-11" (or "Q-43448-11" / "Q-43448-04") into [quoteNumber, revisionNumber].
 *  Returns null when the segment can't be split into two numeric parts. */
function parseQuoteId(raw: string | undefined): [number, number] | null {
  if (!raw) return null;
  // Strip leading "Q-" if present, then split on the last dash to be safe.
  const cleaned = raw.replace(/^Q-/i, '');
  const dash = cleaned.lastIndexOf('-');
  if (dash < 0) return null;
  const n = parseInt(cleaned.slice(0, dash), 10);
  const r = parseInt(cleaned.slice(dash + 1), 10);
  if (Number.isNaN(n) || Number.isNaN(r)) return null;
  return [n, r];
}

/**
 * Quote Detail / Edit page.
 *   /order-management/quotes/:quoteId   (mode=view)  — fetches GET /quotes/:n/:r
 *   /order-management/quotes/new        (mode=create, seeded by ?billToId=…&customer=…)
 *
 * Quote Details header card + Customer Info tab read real backend data. The
 * other tabs (Dray, Warehouse, Delivery, Documents, Notes, History) render
 * their mockup-fixture content.
 */
const QuoteDetailPage: React.FC<Props> = ({ mode }) => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState<string>('cust');
  const [data, setData] = useState<QuoteDetailData | null>(null);
  const [loading, setLoading] = useState(mode === 'view');
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const seedFromUrl: QuoteDetailData = useMemo(() => ({
    ...SAMPLE_QUOTE_DETAIL,
    quoteId: 'NEW',
    quoteNumber: undefined,
    revisionNumber: undefined,
    status: 'UNSECURED',
    statusDate: '',
    // V1 leaves businesstype blank on revision 1 (rev reasons only apply to
    // later revisions — formatview_quote.pl fv_businesstype).
    revReason: '',
    lastUsed: '',
    daysSince: '0',
    effective: '',
    reviewDate: '',
    endDate: '',
    customer: searchParams.get('customer') || SAMPLE_QUOTE_DETAIL.customer,
    billTo: searchParams.get('billToId') || SAMPLE_QUOTE_DETAIL.billTo,
    city: searchParams.get('city') || SAMPLE_QUOTE_DETAIL.city,
    stateCode: searchParams.get('state') || SAMPLE_QUOTE_DETAIL.stateCode,
    zip: searchParams.get('zip') || SAMPLE_QUOTE_DETAIL.zip,
  }), [searchParams]);

  useEffect(() => {
    if (mode === 'create') {
      setData(seedFromUrl);
      setLoading(false);
      return;
    }
    const parsed = parseQuoteId(quoteId);
    if (!parsed) {
      setError(`Invalid quote ID: ${quoteId}`);
      setLoading(false);
      return;
    }
    const [n, r] = parsed;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    quoteService.getQuote(n, r)
      .then((d) => { if (!cancelled) { setData(d); } })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (isAxiosError(e) && e.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, quoteId, seedFromUrl]);

  const goBack = () => navigate('/order-management/quotes');

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f6f8] dark:bg-gray-900">
      {loading && <LoadingShell goBack={goBack} quoteIdLabel={quoteId ?? ''} />}
      {!loading && notFound && (
        <ErrorShell
          title="Quote not found"
          body={`No quote matches ${quoteId}. It may have been deleted, or the URL is wrong.`}
          goBack={goBack}
        />
      )}
      {!loading && error && (
        <ErrorShell
          title="Failed to load quote"
          body={error}
          goBack={goBack}
        />
      )}

      {!loading && !error && !notFound && data && (
        <DetailBody data={data} setData={setData} mode={mode} tab={tab} setTab={setTab} goBack={goBack} />
      )}
    </div>
  );
};

interface BodyProps {
  data: QuoteDetailData;
  setData: (d: QuoteDetailData) => void;
  mode: 'create' | 'view';
  tab: string;
  setTab: (id: string) => void;
  goBack: () => void;
}

const DetailBody: React.FC<BodyProps> = ({ data, setData, mode, tab, setTab, goBack }) => {
  const navigate = useNavigate();
  const isWarehouse = data.quoteType === 'WAREHOUSE';
  const tabs = isWarehouse ? WAREHOUSE_TABS : DIRECT_TABS;
  const [actionMsg, setActionMsg] = React.useState<string | null>(null);
  // Quote Details collapse state — shared per-user setting with the editor
  // drawer, so the section stays collapsed/expanded across reloads and quotes.
  const { collapsed: sectionCollapsed, setSection } = useQuoteSectionCollapse();

  // ── Edit host (view quotes — DIRECT and WAREHOUSE) ─────────────────────────
  // `draft` mirrors the header/lifecycle (camelCase display shape); `draftDirect`
  // / `draftWarehouse` mirror the type-specific tabs. All seed from the loaded
  // quote and re-seed whenever `data` is replaced (initial load, post-save
  // re-fetch, conflict reload). The tabs render the drafts and emit onChange
  // into them; the Save bar diffs the MAPPED subsets against `data`.
  const isDirect = data.quoteType === 'DIRECT';
  const editable = mode === 'view' && (isDirect || isWarehouse) && !!data.quoteNumber;
  // A SECURED quote is locked: every field is read-only EXCEPT End Date (V1
  // parity). `fieldsEditable` gates all the field-editing callbacks; the header
  // keeps `onRevisionChange` (so End Date stays live) plus a `secured` flag, and
  // the action buttons (New Revision / Dupe / Send) + Save remain available.
  const secured = editable && isQuoteSecured(data.rawStatus);
  const fieldsEditable = editable && !secured;

  const [draft, setDraft] = React.useState<QuoteDetailData>(data);
  const [draftDirect, setDraftDirect] = React.useState<DirectFields | null>(data.direct ?? null);
  // Secured "add-only" charges: a secured quote is otherwise locked, but the
  // user may ADD new charges (confirmed before save). Not offered once the end
  // date is in the past — an ended quote is fully locked (front and back).
  const endedPast = isPastEndDate(draft.endDate);
  const chargesAddOnly = secured && !endedPast;
  // The charges card is interactive when normally editable OR in add-only mode.
  const chargesInteractive = fieldsEditable || chargesAddOnly;
  // Move Type stays editable on a SECURED quote that no order has used yet
  // (SAVED lastUsed blank, not ended); once a PRO uses it, it locks with the
  // rest of the lane. Backend mirrors this in apply_save_rules.
  const moveTypeEditable = isMoveTypeEditable(fieldsEditable, secured, data.lastUsed, data.endDate);
  // Secured broken-tariff exemption: a secured (non-ended) quote whose SAVED
  // customer FSC tariff link is broken — never assigned, or pointing at a tariff
  // since deleted from fueldata — may have that tariff (re)assigned; the rest of
  // the tab stays locked. Gated on the SAVED row so the Lookup stays available
  // until saved — after reload the stored tariff re-locks it.
  // Child records (additional charges + stopoffs) are buffered locally — no
  // autosave. `draft*` drive the cards' display; `*Ops` are the pending server
  // calls replayed on Save. Temp rows use a decreasing negative id until Save.
  const [draftCharges, setDraftCharges] = React.useState<TariffRateRow[]>(data.tariffRates ?? []);
  const [draftStopoffs, setDraftStopoffs] = React.useState<StopoffRow[]>(data.directStopoffs ?? []);
  const [chargeOps, setChargeOps] = React.useState<ChargeOp[]>([]);
  const [stopoffOps, setStopoffOps] = React.useState<StopoffOp[]>([]);
  const tempIdRef = React.useRef(-1);
  const nextTempId = () => { const id = tempIdRef.current; tempIdRef.current -= 1; return id; };
  const [draftWarehouse, setDraftWarehouse] = React.useState<WarehouseFields | null>(data.warehouse ?? null);
  // The lane's customer FSC, from whichever sub-object the quote carries --
  // directquote for a DIRECT quote, transloadquote for a WAREHOUSE one.
  const fscLane = laneFsc(data.direct, data.warehouse);
  const tariffDeleted = isDeletedCustomerTariff(fscLane.type, fscLane.tariff, data.fscTariffDeleted);
  const tariffAssignable = secured
    && !isPastEndDate(data.endDate)
    && isBrokenCustomerTariff(fscLane.type, fscLane.tariff, data.fscTariffDeleted);
  const draftTariffNumber = fscLane.lane === 'warehouse'
    ? Number(draftWarehouse?.fuelTariff ?? 0)
    : Number(draftDirect?.fscTariff ?? 0);
  const draftTariffRate = fscLane.lane === 'warehouse'
    ? draftWarehouse?.fuelSurcharge
    : draftDirect?.fscRate;
  // "Assigned in this session" — the draft points somewhere other than the stored
  // (missing or dead) tariff. The compare against the stored number matters for
  // the deleted case, where the stored value is already non-zero.
  const tariffAssignedThisSession = tariffAssignable
    && draftTariffNumber > 0
    && draftTariffNumber !== Number(fscLane.tariff ?? 0);
  const handleAssignTariff = (t: FscTariff) => {
    if (fscLane.lane === 'warehouse') {
      setDraftWarehouse((d) => (d ? {
        ...d,
        fuelType: 'CUSTOMER',
        fuelTariff: t.tariffNumber,
        fuelSurcharge: t.currentRate != null ? String(t.currentRate) : d.fuelSurcharge,
      } : d));
    } else {
      setDraftDirect((d) => (d ? {
        ...d,
        fscType: 'CUSTOMER',
        fscTariff: t.tariffNumber,
        fscRate: t.currentRate != null ? String(t.currentRate) : d.fscRate,
      } : d));
    }
  };
  const [saving, setSaving] = React.useState(false);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [saveErr, setSaveErr] = React.useState<string | null>(null);
  const [conflict, setConflict] = React.useState(false);
  // Secured-quote add-charge confirmation (charges can't be edited once saved).
  const [confirmAddOpen, setConfirmAddOpen] = React.useState(false);
  // Reconcile dupe-check candidates (billto 0→real); non-null opens the warn dialog.
  const [reconcileDupes, setReconcileDupes] = React.useState<DupeCandidate[] | null>(null);
  // v2-migration "verified" state — tracked locally so toggling it never disturbs
  // unsaved field/charge edits (a full reload would re-seed the drafts). Seeded
  // from the loaded quote and re-seeded whenever `data` changes identity (below).
  const [migVerified, setMigVerified] = React.useState(!!data.v2MigrationVerified);
  const [migVerifiedBy, setMigVerifiedBy] = React.useState<string | null>(data.v2MigrationVerifiedBy ?? null);
  const [migVerifiedDate, setMigVerifiedDate] = React.useState<string | null>(data.v2MigrationVerifiedDate ?? null);
  const [migBusy, setMigBusy] = React.useState(false);

  // Header-drag for the reconcile dupe-warning modal.
  const reconcileDupesDrag = useModalDrag(!!reconcileDupes);

  // Re-seed the drafts whenever the loaded quote object changes identity
  // (initial load, post-save re-fetch, conflict reload). We deliberately do
  // NOT clear `saveMsg` here so the "Saved." confirmation survives the
  // post-save re-fetch; save/revert handlers manage the banners explicitly.
  React.useEffect(() => {
    setDraft(data);
    setDraftDirect(data.direct ?? null);
    setDraftCharges(data.tariffRates ?? []);
    setDraftStopoffs(data.directStopoffs ?? []);
    setChargeOps([]);
    setStopoffOps([]);
    setMigVerified(!!data.v2MigrationVerified);
    setMigVerifiedBy(data.v2MigrationVerifiedBy ?? null);
    setMigVerifiedDate(data.v2MigrationVerifiedDate ?? null);
    setDraftWarehouse(data.warehouse ?? null);
  }, [data]);

  const fieldDirty = !!(editable && computeDirty(data, draft, data.direct, draftDirect, data.warehouse, draftWarehouse));
  // Save is enabled by field edits OR any staged charge/stopoff change.
  const isDirty = fieldDirty || chargeOps.length > 0 || stopoffOps.length > 0;
  // N21: a DO NOT BILL storage type must carry a reason; block save until it does
  // (the server guard in P03 is the backstop; this is the inline gate).
  const storageReasonMissing = !!draftWarehouse
    && (draftWarehouse.storageType || '').toUpperCase() === 'DO NOT BILL'
    && !draftWarehouse.storageDoNotBillReason;

  const _perms = useTryPermissions();

  const onHeaderChange = (field: keyof QuoteDetailData, v: string) =>
    setDraft((d) => ({ ...d, [field]: v }));
  // P15 #44: typing the SF account name unlinks the recno; picking a result
  // links both name + recno (persisted via HEADER_MAP salesforcename/recno).
  // Bill-to reconcile (V1 deferred new-bill-to). Typing renames + unlinks
  // (billto → 0, a new bill-to); picking links a real bill-to recno AND
  // re-denormalizes its master address onto the quote (V1 reads the master
  // address once billto>0). The dupe-check + active-check run on save.
  // A CUSTOMER FSC tariff is bound to the bill-to — changing it invalidates the
  // FSC type / rate / tariff (which live in draftDirect), so clear them. ONLY
  // the CUSTOMER setup: other fuel types (CURRENT/FLAT/PCT%/…) aren't bill-to
  // -bound, and Fuel Type is now required to save, so wiping them would force a
  // pointless re-pick on every bill-to reconcile.
  const clearDirectFsc = () =>
    setDraftDirect((dd) => (dd && dd.fscType === 'CUSTOMER'
      ? { ...dd, fscType: '', fscRate: '', fscTariff: null } : dd));
  const onHeaderTypeBillTo = (name: string) => {
    if ((draft.billToNumber ?? 0) !== 0) clearDirectFsc(); // unlinking from a real bill-to
    setDraft((d) => ({ ...d, billTo: name, billToNumber: 0 }));
  };
  const onHeaderPickBillTo = (row: BillToLookupRow) => {
    if (row.billto !== (draft.billToNumber ?? 0)) clearDirectFsc();
    setDraft((d) => ({
      ...d, billToNumber: row.billto, billTo: row.billtoname,
      addr1: row.address1, addr2: row.address2, city: row.city,
      stateCode: row.state, zip: row.zip, billFax: row.billtofax,
    }));
  };
  const onRevisionChange = (field: keyof QuoteDetailData, v: string) =>
    setDraft((d) => {
      const next = { ...d, [field]: v };
      // Mirror V1's updatesecuredate(): securing a quote (rawStatus → SECURED)
      // stamps today's secure date when none is set. The Secured Date field is
      // display-only (not hand-editable); this stamp persists via REVISION_MAP.
      if (field === 'rawStatus' && v === 'SECURED' && !String(d.statusDate ?? '').trim()) {
        next.statusDate = todayDisplay();
      }
      return next;
    });
  const onDirectChange = (field: keyof DirectFields, v: string) =>
    setDraftDirect((d) => (d ? { ...d, [field]: v } : d));
  // P10 #22: picking a cms.locations row fills the denormalized deliver-to
  // address + binds delivertocode (frozen by N18 until the name is re-typed).
  const onDirectPickLocation = (row: LocationLookupRow) =>
    setDraftDirect((d) => (d ? {
      ...d,
      deliverToName: row.lname,
      deliverToAddr1: row.address1,
      deliverToAddr2: row.address2,
      deliverToCity: row.city,
      deliverToState: row.state,
      deliverToZip: row.zip,
      deliverToCode: row.locationcode,
    } : d));
  const onWarehouseChange = (field: keyof WarehouseFields, v: string) =>
    setDraftWarehouse((d) => (d ? { ...d, [field]: v } : d));

  // Est. Revenue = (dray w/charges + FSC adjustment) × volume (V1
  // updateestimatedrevenue). Owned by the page so it survives sub-tab switches.
  useEstimatedRevenue({
    dray: draftDirect?.drayWithCharges ?? '',
    fsctype: draftDirect?.fscType ?? '',
    fsctariff: draftDirect?.fscTariff != null ? String(draftDirect.fscTariff) : '',
    deliveryzone: draftDirect?.deliveryZone ?? '',
    fscrate: draftDirect?.fscRate ?? '',
    miles: draftDirect?.deliverToMiles ?? '',
    volume: draft.volume ?? '',
    estRev: draft.estRev ?? '',
    enabled: fieldsEditable && !!draftDirect,
    onEstRev: (v) => onRevisionChange('estRev', v),
  });

  const revert = () => {
    setDraft(data);
    setDraftDirect(data.direct ?? null);
    setDraftCharges(data.tariffRates ?? []);
    setDraftStopoffs(data.directStopoffs ?? []);
    setChargeOps([]);
    setStopoffOps([]);
    setDraftWarehouse(data.warehouse ?? null);
    setSaveMsg(null);
    setSaveErr(null);
    setConflict(false);
  };

  const reloadFromServer = React.useCallback(async () => {
    if (!data.quoteNumber || data.revisionNumber == null) return;
    const fresh = await quoteService.getQuote(data.quoteNumber, data.revisionNumber);
    setData(fresh); // re-seeds drafts via the effect above
  }, [data.quoteNumber, data.revisionNumber, setData]);

  // v2-migration "verified" toggle. Shown only for migrated quotes in view mode;
  // backend enforces write access (require_write_access → 403). App-owned write,
  // so it updates only the local badge state — unsaved field edits are untouched.
  const canVerifyMigration = mode === 'view' && !!data.v2QuoteNumber && !!data.quoteNumber;
  const handleToggleMigrationVerified = async (next: boolean) => {
    if (!data.quoteNumber) return;
    setMigBusy(true);
    setSaveErr(null);
    try {
      const res = await quoteService.verifyMigration(data.quoteNumber, next);
      setMigVerified(res.v2MigrationVerified);
      setMigVerifiedBy(res.v2MigrationVerifiedBy);
      setMigVerifiedDate(res.v2MigrationVerifiedDate);
      setSaveMsg(res.v2MigrationVerified ? 'Migration verified.' : 'Migration verification cleared.');
    } catch (e) {
      setSaveErr(saveErrorMessage(e) || 'Failed to update migration verification.');
    } finally {
      setMigBusy(false);
    }
  };

  // Reconcile dupe-check params from the picked bill-to + the quote's lane
  // (V1 checkfordupes, run when the bill-to is "filled in later").
  const reconcileDupeParams = (): DupeCheckParams => {
    const base: DupeCheckParams = {
      quotetype: draftDirect ? 'DIRECT' : 'WAREHOUSE',
      billto: draft.billToNumber ?? 0,
      billtoname: draft.billTo ?? '',
    };
    if (draftDirect) {
      return {
        ...base,
        ratezone: draftDirect.rateZone ?? '',
        deliveryzone: draftDirect.deliveryZone ?? '',
        delivertozip: draftDirect.deliverToZip ?? '',
        delivertocity: draftDirect.deliverToCity ?? '',
        delivertostate: draftDirect.deliverToState ?? '',
      };
    }
    if (draftWarehouse) {
      return {
        ...base,
        movetype: draftWarehouse.moveType ?? '',
        whsetype: draftWarehouse.whseType ?? '',
        packagetype: draftWarehouse.packageType ?? '',
        warehouse: draftWarehouse.warehouse ?? '',
        size: draftWarehouse.size ?? '',
        equiptype: draftWarehouse.equipType ?? '',
      };
    }
    return base;
  };

  // Returns true when the save succeeded (or there was nothing to save), false
  // on error/conflict. Persists field edits AND replays staged charge/stopoff
  // ops in one Save — quotes never autosave.
  const doSave = async (): Promise<boolean> => {
    if (!editable || !isDirty) return true;
    if (saving || storageReasonMissing || saveBlockedByMin) return false;
    if (!data.quoteNumber || data.revisionNumber == null) return false;
    const qn = data.quoteNumber;
    const rev = data.revisionNumber;
    setSaving(true);
    setSaveErr(null);
    setSaveMsg(null);
    setConflict(false);
    try {
      // Patch the field draft only when a field actually changed (a charges-only
      // save sends no field PATCH), then replay the staged child ops.
      if (fieldDirty) {
        const { payload } = buildUpdatePayload(data, draft, data.direct, draftDirect, data.warehouse, draftWarehouse);
        await quoteService.updateQuote(qn, rev, payload);
      }
      await replayChargeOps(quoteService, qn, rev, chargeOps);
      await replayStopoffOps(quoteService, qn, rev, stopoffOps);
      const fresh = await quoteService.getQuote(qn, rev);
      setData(fresh); // clears dirty + re-seeds drafts + staged ops
      setSaveMsg('Saved.');
      return true;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 409) {
        setConflict(true);
      } else {
        setSaveErr(saveErrorMessage(e));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (saving || !editable || !isDirty || storageReasonMissing || saveBlockedByMin) return;
    if (!data.quoteNumber || data.revisionNumber == null) return;
    // Review Date is required to save.
    if (!(draft.reviewDate ?? '').trim()) { setSaveErr('Review Date is required.'); return; }
    // Delivery Type likewise (DIRECT only — deliveryzone is a directquote column).
    if (draftDirect && !(draftDirect.deliveryZone ?? '').trim()) {
      setSaveErr('Delivery Type is required.'); return;
    }
    // Fuel Type likewise — a blank fsctype means the invoice never charges FSC.
    if (draftDirect && !(draftDirect.fscType ?? '').trim()) {
      setSaveErr('Fuel Type is required.'); return;
    }
    // Fuel Calculation + Rate Applies are masterquote fields, so they apply to
    // the quote itself rather than to a DIRECT lane — no draftDirect guard.
    // Together they decide which pro date prices the quote; a blank Rate Applies
    // leaves an expired quote with no date to validate the rate against.
    if (!(draft.fuelCalc ?? '').trim()) { setSaveErr('Fuel Calculation is required.'); return; }
    if (!(draft.rateApplies ?? '').trim()) { setSaveErr('Rate Applies is required.'); return; }
    // Reconcile: assigning a real bill-to to a deferred (billto=0) quote runs
    // the dupe-check V1 skipped at create. Warn but allow Continue.
    if (data.billToNumber === 0 && (draft.billToNumber ?? 0) > 0) {
      try {
        const dupes = await quoteService.dupeCheck(reconcileDupeParams());
        if (dupes.length > 0) { setReconcileDupes(dupes); return; }
      } catch {
        // A dupe-check failure must not block the save (V1 proceeds).
      }
    }
    // A secured quote's two permanent deviations (a newly-added charge or a
    // freshly-assigned missing tariff) are confirmed before Save persists them.
    if (secured && (chargeOps.some((op) => op.kind === 'add') || tariffAssignedThisSession)) {
      setConfirmAddOpen(true);
      return;
    }
    await doSave();
  };

  const handleConfirmAddCharges = async () => {
    await doSave();            // surfaces success / error / conflict in the page chrome
    setConfirmAddOpen(false);  // close regardless so any error banner is visible
  };

  const handleConflictReload = async () => {
    setConflict(false);
    try {
      await reloadFromServer();
    } catch {
      setSaveErr('Failed to reload the quote. Please refresh the page.');
    }
  };

  // ── Charge (tariff-rate) + stopoff editing — editable DIRECT quotes only ──
  // Edits are buffered locally (no autosave) and replayed on Save.
  const [chargeCatalog, setChargeCatalog] = useState<ChargeCatalogRow[]>([]);
  useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    quoteService.chargeCatalog(isDirect ? 'direct' : 'transload')
      .then((rows) => { if (!cancelled) setChargeCatalog(rows); })
      .catch(() => { /* add-row select just renders empty */ });
    return () => { cancelled = true; };
  }, [editable, isDirect]);

  // Live rate-zone list (cms.ratezones via /quotes/ratezones). value = ratezone
  // CODE (stored), label = displayname (shown) — V1 select_html5.pl parity.
  // Falls back to the static RATE_ZONES constant inside the tabs when empty.
  const [rateZoneOptions, setRateZoneOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    quoteService.rateZones()
      .then((rows) => {
        if (!cancelled) {
          setRateZoneOptions(
            rows.filter((r) => r.rateZone).map((r) => ({ value: r.rateZone, label: r.displayName || r.rateZone })),
          );
        }
      })
      .catch(() => { /* tabs fall back to the static list */ });
    return () => { cancelled = true; };
  }, []);

  // Delivery-lane charges use the 'delivery' charge scope (V1 additionalcharges
  // with delivery='Y'). Loaded separately for WAREHOUSE quotes only.
  const [deliveryChargeCatalog, setDeliveryChargeCatalog] = useState<ChargeCatalogRow[]>([]);
  useEffect(() => {
    if (!editable || isDirect) return;
    let cancelled = false;
    quoteService.chargeCatalog('delivery')
      .then((rows) => { if (!cancelled) setDeliveryChargeCatalog(rows); })
      .catch(() => { /* add-row select just renders empty */ });
    return () => { cancelled = true; };
  }, [editable, isDirect]);

  const chargeOp = async (fn: () => Promise<unknown>) => {
    if (!data.quoteNumber || data.revisionNumber == null) return false;
    if (isDirty) {
      setSaveErr('Save or revert your field changes before editing charges.');
      return false;
    }
    setSaveErr(null);
    try {
      await fn();
      await reloadFromServer();
      return true;
    } catch (e: unknown) {
      setSaveErr(saveErrorMessage(e));
      return false;
    }
  };

  // A lane charge pins tablename='deliveryquote' + the lane recno (tablerecno).
  const handleAddLaneCharge = (laneRecno: number, p: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number }) =>
    chargeOp(() => quoteService.addCharge(data.quoteNumber!, data.revisionNumber!,
      { ...p, tablename: 'deliveryquote', tablerecno: laneRecno }));
  const handleAddCharge = (p: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => {
    const tempId = nextTempId();
    const desc = chargeCatalog.find((c) => c.recno === p.additionalchargesrecno)?.description ?? '';
    setDraftCharges((rows) => [...rows, {
      id: tempId, description: desc, rateType: p.addlchargeratetype ?? '',
      rate: String(p.addlchargerate ?? ''), scope: isDirect ? 'directquote' : 'transloadquote', pdfMessage: '',
      freeTime: p.freetime != null ? String(p.freetime) : '', freeTimeUnit: p.freetimeunit ?? '',
      additionalType: p.additionaltype ?? '', additionalDays: p.additionaldays ?? 0,
    }]);
    setChargeOps((ops) => stageChargeAdd(ops, tempId, { ...p, tablename: isDirect ? 'directquote' : 'transloadquote' }));
  };
  const handleChangeCharge = (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => {
    // On a secured quote only THIS-session additions (negative temp id) may be
    // edited; charges already saved to the quote are locked.
    if (secured && id >= 0) return;
    setDraftCharges((rows) => rows.map((r) => r.id === id ? {
      ...r,
      ...(patch.addlchargeratetype !== undefined ? { rateType: patch.addlchargeratetype } : {}),
      ...(patch.addlchargerate !== undefined ? { rate: String(patch.addlchargerate) } : {}),
      ...(patch.freetime !== undefined ? { freeTime: String(patch.freetime) } : {}),
      ...(patch.freetimeunit !== undefined ? { freeTimeUnit: patch.freetimeunit } : {}),
      ...(patch.additionaltype !== undefined ? { additionalType: patch.additionaltype } : {}),
      ...(patch.additionaldays !== undefined ? { additionalDays: patch.additionaldays } : {}),
    } : r));
    setChargeOps((ops) => stageChargeUpdate(ops, id, patch));
  };
  const handleDeleteCharge = (id: number) => {
    // Secured: a saved charge can't be removed; only this-session drafts.
    if (secured && id >= 0) return;
    if (!window.confirm('Remove this charge from the quote?')) return;
    setDraftCharges((rows) => rows.filter((r) => r.id !== id));
    setChargeOps((ops) => stageChargeDelete(ops, id));
  };

  // ── Handling-item CRUD (WAREHOUSE quotes) — same round-trip pattern ──────
  const [handlingCatalog, setHandlingCatalog] = useState<HandlingCatalogRow[]>([]);
  useEffect(() => {
    if (!editable || isDirect) return;
    let cancelled = false;
    quoteService.handlingCatalog()
      .then((rows) => { if (!cancelled) setHandlingCatalog(rows); })
      .catch(() => { /* add-row select just renders empty */ });
    return () => { cancelled = true; };
  }, [editable, isDirect]);

  const handleAddHandling = (p: { handlingitemrecno: number; handlingtype: 'handlingin' | 'handlingout'; handlingratetype: string; handlingrate: number; handlingunittype?: string }) =>
    chargeOp(() => quoteService.addHandling(data.quoteNumber!, data.revisionNumber!, p));
  const handleChangeHandling = (id: number, patch: { handlingratetype?: string; handlingrate?: number; handlingunittype?: string }) =>
    chargeOp(() => quoteService.updateHandling(data.quoteNumber!, data.revisionNumber!, id, patch));
  const handleDeleteHandling = (id: number) => {
    if (!window.confirm('Remove this handling item?')) return;
    void chargeOp(() => quoteService.deleteHandling(data.quoteNumber!, data.revisionNumber!, id));
  };

  // ── Standards/minimum-rate guardrails (V1 draystandards et al.) ──────────
  const [rateStandards, setRateStandards] = useState<RateStandards | null>(null);
  useEffect(() => {
    if (!editable || isDirect) return;  // dray/delivery guards are WAREHOUSE-side
    let cancelled = false;
    quoteService.rateStandards()
      .then((std) => { if (!cancelled) setRateStandards(std); })
      .catch(() => { /* warnings simply don't render */ });
    return () => { cancelled = true; };
  }, [editable, isDirect]);

  // #13: hard-block save on a below-minimum standard rate (warehouse dray +
  // transload/handling) unless the user can bypass. DORMANT until the bypass AD
  // group is configured (minRateBlocksSave returns false while it's empty).
  const _whRate = editable && draftWarehouse ? draftWarehouse : data.warehouse;
  const anyRateBelowMin = !!_whRate && (
    belowMinimum(drayGuard(rateStandards, _whRate.warehouse, _whRate.rateZone), _whRate.drayRate)
    || belowMinimum(
      transloadGuard(rateStandards, _whRate.whseType, _whRate.packageType, _whRate.moveType, _whRate.size),
      _whRate.drayHandlingCharge,
    )
  );
  const saveBlockedByMin = minRateBlocksSave(
    anyRateBelowMin,
    QUOTE_BYPASS_MINIMUM_RATE_GROUP_ID,
    !!QUOTE_BYPASS_MINIMUM_RATE_GROUP_ID && !!_perms?.isInGroups([QUOTE_BYPASS_MINIMUM_RATE_GROUP_ID]),
  );

  // ── Product-rate CRUD (WAREHOUSE quotes) — same round-trip pattern ───────
  const handleAddProductRate = (p: ProductRateFieldsPayload) =>
    chargeOp(() => quoteService.addProductRate(data.quoteNumber!, data.revisionNumber!, p));
  const handleUpdateProductRate = (id: number, p: ProductRateFieldsPayload) =>
    chargeOp(() => quoteService.updateProductRate(data.quoteNumber!, data.revisionNumber!, id, p));
  const handleDeleteProductRate = (id: number) => {
    if (!window.confirm('Remove this product rate?')) return;
    void chargeOp(() => quoteService.deleteProductRate(data.quoteNumber!, data.revisionNumber!, id));
  };

  const handleAddLaneStopoff = (laneId: number, payload: StopoffFieldsPayload) =>
    chargeOp(() => quoteService.addStopoff(data.quoteNumber!, data.revisionNumber!, { ...payload, laneRecno: laneId }));
  // ── Stopoff editing — directquote-pinned, buffered until Save ─────────────
  const handleAddDirectStopoff = (payload: StopoffFieldsPayload) => {
    const tempId = nextTempId();
    setDraftStopoffs((rows) => [...rows, stopoffRowFromPayload(payload, tempId, rows.length)]);
    setStopoffOps((ops) => stageStopoffAdd(ops, tempId, payload));
  };
  const handleUpdateStopoff = (stopId: number, payload: StopoffFieldsPayload) => {
    setDraftStopoffs((rows) => rows.map((r) => r.stopId === stopId ? { ...r, ...stopoffRowPatch(payload) } : r));
    setStopoffOps((ops) => stageStopoffUpdate(ops, stopId, payload));
  };
  const handleDeleteStopoff = (stopId: number) => {
    if (!window.confirm('Remove this stopoff?')) return;
    setDraftStopoffs((rows) => rows.filter((r) => r.stopId !== stopId));
    setStopoffOps((ops) => stageStopoffDelete(ops, stopId));
  };

  // ── Delivery-lane CRUD (WAREHOUSE quotes) — same round-trip pattern ──────
  const handleAddLane = (payload: LaneFieldsPayload) =>
    chargeOp(() => quoteService.addLane(data.quoteNumber!, data.revisionNumber!, payload));
  const handleSaveLane = (laneId: number, payload: LaneFieldsPayload) =>
    chargeOp(() => quoteService.updateLane(data.quoteNumber!, data.revisionNumber!, laneId, payload));
  const handleDeleteLane = (laneId: number) => {
    if (!window.confirm('Delete this delivery lane?')) return;
    void chargeOp(() => quoteService.deleteLane(data.quoteNumber!, data.revisionNumber!, laneId));
  };

  // If the active tab id isn't valid for this quote type, fall back to the
  // first tab in the visible set. Avoids dead-clicks when switching between
  // a WAREHOUSE quote (with tab=whse) and a DIRECT quote.
  React.useEffect(() => {
    if (!tabs.some((t) => t.id === tab)) setTab(tabs[0].id);
  }, [tabs, tab, setTab]);

  // Minimal leave-guard: warn before navigating away with unsaved edits.
  const guardedGoBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    goBack();
  };

  // ── Send Quote (V1 viewsendquote / quotePDFEmail) ────────────────────────
  const [sendDialogOpen, setSendDialogOpen] = React.useState(false);
  const handleSendQuote = () => {
    if (!data.quoteNumber || data.revisionNumber == null) {
      setActionMsg('Save the quote first — the emailed PDF renders from saved data.');
      return;
    }
    setActionMsg(null);
    setSendDialogOpen(true);
  };

  // ── View PDF (V1 'View Quote' — pdf_quote.pl) ────────────────────────────
  const handleViewPdf = async () => {
    if (!data.quoteNumber || data.revisionNumber == null) {
      setActionMsg('Save the quote first — the PDF renders from saved data.');
      return;
    }
    const opened = await openQuotePdf(data.quoteNumber, data.revisionNumber);
    if (!opened) setActionMsg('Failed to render the quote PDF. Please try again.');
  };

  // Field/action ACLs (P18 #46) — hide gated actions the user can't perform.
  // Fail-open: dormant or unresolved → every key held → buttons show as today.
  const quoteAcls = useQuoteAcls();
  const canDupe = quoteAcls.has('dupe');
  // New Revision is only offered on the LATEST revision (nextRevision == null —
  // no later revision exists); older revisions are view/dupe only.
  const isLatestRevision = data.nextRevision == null;
  const canNewRevision = quoteAcls.has('newrevision') && isLatestRevision;

  // ── Dupe Quote (V1 editrecord_quote.pl:4751-5014) ────────────────────────
  const [dupeDialogOpen, setDupeDialogOpen] = React.useState(false);
  const [duping, setDuping] = React.useState(false);

  const handleDupe = () => {
    if (!data.quoteNumber) {
      setActionMsg('Cannot dupe an unsaved quote. Save the quote first.');
      return;
    }
    if (isDirty) {
      setActionMsg('Save or revert your changes before duplicating the quote.');
      return;
    }
    setActionMsg(null);
    setDupeDialogOpen(true);
  };

  const submitDupe = async (payload: RevisionCreatePayload) => {
    if (!data.quoteNumber || data.revisionNumber == null || duping) return;
    setDuping(true);
    try {
      const result = await quoteService.dupeQuote(data.quoteNumber, data.revisionNumber, payload);
      setDupeDialogOpen(false);
      warnDroppedCharges(result.droppedCharges);
      navigate(`/order-management/quotes/${result.quoteId}`);
    } catch (e: unknown) {
      setActionMsg(saveErrorMessage(e));
      setDupeDialogOpen(false);
    } finally {
      setDuping(false);
    }
  };

  // ── New Revision (V1 editrecord_quote.pl:1376-1641) ──────────────────────
  const [revisionDialogOpen, setRevisionDialogOpen] = React.useState(false);
  const [creatingRevision, setCreatingRevision] = React.useState(false);

  const handleNewRevision = () => {
    if (!data.quoteNumber) {
      setActionMsg('Cannot create a new revision of an unsaved quote. Save first.');
      return;
    }
    if (isDirty) {
      setActionMsg('Save or revert your changes before creating a new revision.');
      return;
    }
    setActionMsg(null);
    setRevisionDialogOpen(true);
  };

  const submitNewRevision = async (payload: RevisionCreatePayload) => {
    if (!data.quoteNumber || creatingRevision) return;
    setCreatingRevision(true);
    try {
      const result = await quoteService.createRevision(data.quoteNumber, payload);
      setRevisionDialogOpen(false);
      warnDroppedCharges(result.droppedCharges);
      navigate(`/order-management/quotes/${result.quoteId}`);
    } catch (e: unknown) {
      setActionMsg(saveErrorMessage(e));
      setRevisionDialogOpen(false);
    } finally {
      setCreatingRevision(false);
    }
  };

  return (
    <>
      {revisionDialogOpen && data.quoteNumber && (
        <NewRevisionDialog
          open={revisionDialogOpen}
          quoteNumber={data.quoteNumber}
          quoteType={data.quoteType}
          currentRevision={data.revisionNumber ?? 0}
          busy={creatingRevision}
          onSubmit={(p) => { void submitNewRevision(p); }}
          onCancel={() => setRevisionDialogOpen(false)}
        />
      )}
      {sendDialogOpen && data.quoteNumber && data.revisionNumber != null && (
        <SendQuoteDialog
          open={sendDialogOpen}
          quoteNumber={data.quoteNumber}
          revisionNumber={data.revisionNumber}
          onClose={() => setSendDialogOpen(false)}
        />
      )}
      {dupeDialogOpen && data.quoteNumber && (
        <NewRevisionDialog
          open={dupeDialogOpen}
          mode="dupe"
          quoteNumber={data.quoteNumber}
          quoteType={data.quoteType}
          currentRevision={data.revisionNumber ?? 0}
          busy={duping}
          onSubmit={(p) => { void submitDupe(p); }}
          onCancel={() => setDupeDialogOpen(false)}
        />
      )}
      {/* Reconcile dupe warning (V1 checkfordupes, run when the bill-to is
          filled in later). Warn but allow Save Anyway. */}
      {reconcileDupes && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Duplicate Quotes Found"
        >
          <div
            ref={reconcileDupesDrag.panelRef}
            style={reconcileDupesDrag.panelStyle}
            className="w-full max-w-[560px] bg-white border border-amber-300 rounded-[10px] overflow-hidden shadow-2xl dark:bg-slate-800 dark:border-amber-700"
          >
            <ModalHeader
              title="Duplicate Quotes Found"
              onClose={() => setReconcileDupes(null)}
              dragHandleProps={reconcileDupesDrag.dragHandleProps}
            />
            <div className="px-4 py-3 border-b border-slate-200 bg-amber-50 dark:border-slate-700 dark:bg-amber-900/30">
              <p className="m-0 text-[0.71875rem] text-amber-800 dark:text-amber-300">
                {reconcileDupes.length} existing quote{reconcileDupes.length === 1 ? '' : 's'} match this
                bill-to, type, and lane. Open one to edit it, or save this bill-to anyway.
              </p>
            </div>
            <div className="px-4 py-3 max-h-[40vh] overflow-y-auto flex flex-col gap-1.5">
              {reconcileDupes.map((c) => (
                <button
                  key={`${c.quoteNumber}-${c.revisionNumber}`}
                  type="button"
                  onClick={() => { setReconcileDupes(null); navigate(`/order-management/quotes/${c.quoteNumber}-${c.revisionNumber}`); }}
                  className="w-full text-left px-3 py-2 border border-slate-200 rounded-md hover:bg-blue-50 hover:border-blue-300 dark:border-slate-600 dark:hover:bg-slate-700"
                >
                  <div className="text-[0.78125rem] font-semibold text-slate-900 dark:text-slate-100">
                    Q-{c.quoteNumber}-{c.revisionNumber} — {c.billtoname}
                  </div>
                  <div className="text-[0.71875rem] text-slate-500 dark:text-slate-400">{c.lane}</div>
                </button>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 dark:border-slate-700 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => setReconcileDupes(null)}
                className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setReconcileDupes(null); void doSave(); }}
                className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded-md cursor-pointer hover:bg-blue-700"
              >
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmSecuredChangesModal
        open={confirmAddOpen}
        charges={draftCharges.filter((r) => r.id < 0)}
        tariff={tariffAssignedThisSession
          ? { tariffNumber: draftTariffNumber, rate: draftTariffRate }
          : null}
        busy={saving}
        onConfirm={handleConfirmAddCharges}
        onCancel={() => setConfirmAddOpen(false)}
      />
      {actionMsg && (
        <div className="bg-amber-50 border-b border-amber-300 px-[22px] py-2.5 flex items-start justify-between gap-3">
          <div className="text-[0.78125rem] text-amber-900 flex-1">{actionMsg}</div>
          <button
            type="button"
            onClick={() => setActionMsg(null)}
            className="text-amber-600 hover:text-amber-900 text-[0.8125rem] font-bold leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {conflict && (
        <div className="bg-red-50 border-b border-red-300 px-[22px] py-2.5 flex items-start justify-between gap-3" role="alert">
          <div className="text-[0.78125rem] text-red-900 flex-1">
            This quote was changed by someone else since you opened it. Reload to get the latest
            (your unsaved edits will be lost).
          </div>
          <button
            type="button"
            onClick={handleConflictReload}
            className="px-3 py-1 text-[0.75rem] font-semibold text-white bg-red-600 border border-red-700 rounded-md cursor-pointer hover:bg-red-700 flex-shrink-0"
          >
            Reload
          </button>
        </div>
      )}
      {saveErr && (
        <div className="bg-red-50 border-b border-red-300 px-[22px] py-2.5 flex items-start justify-between gap-3" role="alert">
          <div className="text-[0.78125rem] text-red-900 flex-1">{saveErr}</div>
          <button
            type="button"
            onClick={() => setSaveErr(null)}
            className="text-red-600 hover:text-red-900 text-[0.8125rem] font-bold leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {saveMsg && (
        <div className="bg-green-50 border-b border-green-300 px-[22px] py-2.5 flex items-start justify-between gap-3" role="status">
          <div className="text-[0.78125rem] text-green-900 flex-1">{saveMsg}</div>
          <button
            type="button"
            onClick={() => setSaveMsg(null)}
            className="text-green-600 hover:text-green-900 text-[0.8125rem] font-bold leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      <div className="bg-panel px-[22px] pt-3.5 pb-2.5 flex items-center justify-between gap-4 flex-nowrap flex-shrink-0">
        <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 tracking-[-0.01em]">
          <span className="text-blue-600 font-semibold">Quote {data.quoteId}</span>
          <StatusPill status={data.status} />
          <TypePill type={data.quoteType} />
          <MigrationVerifyBadge
            tracked={!!data.v2MigrationTracked}
            v2QuoteNumber={data.v2QuoteNumber}
            v2Lane={data.v2Lane}
            verified={migVerified}
            verifiedBy={migVerifiedBy}
            verifiedDate={migVerifiedDate}
            canVerify={canVerifyMigration && !!data.v2MigrationTracked}
            busy={migBusy}
            onToggle={handleToggleMigrationVerified}
          />
          <span className="text-[0.78125rem] text-slate-500 dark:text-slate-400 font-normal">· {data.customer}</span>
        </h1>
        <div className="flex gap-2">
          <GhostBtn onClick={guardedGoBack}>← Back to Quotes</GhostBtn>
          <GhostBtn onClick={guardedGoBack}>Cancel</GhostBtn>
          <GhostBtn onClick={handleViewPdf}>View PDF</GhostBtn>
          {/* Same PDF View PDF renders, spooled to a CUPS laser printer (print-send gated). */}
          {!!data.quoteNumber && data.revisionNumber != null && (
            <PrintButton
              endpoint={`/quotes/${data.quoteNumber}/${data.revisionNumber}/print`}
              title="Print Quote"
              className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 inline-flex items-center gap-1.5"
            />
          )}
          <GhostBtn onClick={handleSendQuote}>Send…</GhostBtn>
          {canDupe && <GhostBtn onClick={handleDupe}>Dupe</GhostBtn>}
          {canNewRevision && <GhostBtn onClick={handleNewRevision}>New Revision</GhostBtn>}
          {editable && (
            <SaveBtn onClick={handleSave} disabled={!isDirty || saving || storageReasonMissing || saveBlockedByMin} label={saving ? 'Saving…' : undefined} />
          )}
        </div>
      </div>

      <QuoteDetailHeader
        data={editable ? draft : data}
        onRevisionChange={editable ? onRevisionChange : undefined}
        secured={secured}
        collapsed={sectionCollapsed.quoteDetails}
        onCollapsedChange={(c) => setSection('quoteDetails', c)}
      />

      <QuoteDetailTabs active={tab} onChange={setTab} tabs={tabs} />

      <div className="flex flex-col gap-3.5 px-[18px] py-3.5">
        {tab === 'cust' && (
          <CustomerInfoTab
            data={editable ? draft : data}
            onHeaderChange={fieldsEditable ? onHeaderChange : undefined}
            onTypeBillTo={fieldsEditable ? onHeaderTypeBillTo : undefined}
            onPickBillTo={fieldsEditable ? onHeaderPickBillTo : undefined}
          />
        )}
        {tab === 'dray' && data.warehouse && (
          <DrayTab
            data={warehouseWithTotals(fieldsEditable && draftWarehouse ? draftWarehouse : data.warehouse, draftCharges, data.handlingItems ?? [])}
            tariffRates={draftCharges.filter((r) => r.scope === 'transloadquote')}
            onWarehouseChange={fieldsEditable && draftWarehouse ? onWarehouseChange : undefined}
            chargesEditable={fieldsEditable}
            chargeCatalog={chargeCatalog}
            onAddCharge={fieldsEditable ? handleAddCharge : undefined}
            onChangeCharge={fieldsEditable ? handleChangeCharge : undefined}
            onDeleteCharge={fieldsEditable ? handleDeleteCharge : undefined}
            rateZoneOptions={rateZoneOptions.map((o) => o.value)}
            rateStandards={rateStandards}
            billTo={data.billToNumber ?? undefined}
            onAssignTariff={tariffAssignable ? handleAssignTariff : undefined}
            deletedTariffNumber={tariffDeleted ? Number(data.warehouse?.fuelTariff) : null}
          />
        )}
        {tab === 'dray' && !data.warehouse && <MissingDataPanel kind="WAREHOUSE" />}
        {tab === 'whse' && data.warehouse && (
          <WarehouseTab
            data={warehouseWithTotals(fieldsEditable && draftWarehouse ? draftWarehouse : data.warehouse, draftCharges, data.handlingItems ?? [])}
            onWarehouseChange={fieldsEditable && draftWarehouse ? onWarehouseChange : undefined}
            rateStandards={rateStandards}
            handlingItems={data.handlingItems ?? []}
            handlingCatalog={handlingCatalog}
            onAddHandling={fieldsEditable ? handleAddHandling : undefined}
            onChangeHandling={fieldsEditable ? handleChangeHandling : undefined}
            onDeleteHandling={fieldsEditable ? handleDeleteHandling : undefined}
            onAddProductRate={fieldsEditable ? handleAddProductRate : undefined}
            onUpdateProductRate={fieldsEditable ? handleUpdateProductRate : undefined}
            onDeleteProductRate={fieldsEditable ? handleDeleteProductRate : undefined}
            onProductCodeLookup={fieldsEditable ? (code) => quoteService.productCodeLookup(code) : undefined}
            onWhseCustomerLookup={fieldsEditable ? (qq) => quoteService.lookupWhseCustomer(qq) : undefined}
          />
        )}
        {tab === 'whse' && !data.warehouse && <MissingDataPanel kind="WAREHOUSE" />}
        {tab === 'direct' && data.direct && (
          <DirectTab
            data={editable && draftDirect ? draftDirect : data.direct}
            tariffRates={draftCharges.filter((r) => r.scope === 'directquote')}
            onDirectChange={fieldsEditable && draftDirect ? onDirectChange : undefined}
            onDirectPickLocation={fieldsEditable && draftDirect ? onDirectPickLocation : undefined}
            chargesEditable={chargesInteractive}
            chargesAddOnly={chargesAddOnly}
            chargeCatalog={chargeCatalog}
            onAddCharge={chargesInteractive ? handleAddCharge : undefined}
            onChangeCharge={chargesInteractive ? handleChangeCharge : undefined}
            onDeleteCharge={chargesInteractive ? handleDeleteCharge : undefined}
            stopoffs={draftStopoffs}
            onAddStopoff={fieldsEditable ? handleAddDirectStopoff : undefined}
            onUpdateStopoff={fieldsEditable ? handleUpdateStopoff : undefined}
            onDeleteStopoff={fieldsEditable ? handleDeleteStopoff : undefined}
            quoteSaved={(data.quoteNumber ?? 0) > 0}
            rateZoneOptions={rateZoneOptions}
            billTo={data.billToNumber ?? undefined}
            onAssignTariff={tariffAssignable ? handleAssignTariff : undefined}
            deletedTariffNumber={tariffDeleted ? Number(fscLane.tariff) : null}
            moveFieldsLocked={(data.revisionNumber ?? 0) > 1}
            onMoveTypeChange={moveTypeEditable && draftDirect ? (v) => onDirectChange('moveType', v) : undefined}
          />
        )}
        {tab === 'direct' && !data.direct && <MissingDataPanel kind="DIRECT" />}
        {tab === 'deliv' && (
          <DeliveryTab
                rateZone={draftWarehouse?.rateZone ?? data.warehouse?.rateZone ?? ''}
            lanes={data.deliveryLanes ?? []}
            tariffRates={(data.tariffRates ?? []).filter((r) => r.scope === 'deliveryquote')}
            lanesEditable={fieldsEditable}
            onAddLane={editable && !endedPast ? handleAddLane : undefined}
            onSaveLane={fieldsEditable ? handleSaveLane : undefined}
            onDeleteLane={fieldsEditable ? handleDeleteLane : undefined}
            onAddStopoff={fieldsEditable ? handleAddLaneStopoff : undefined}
            onUpdateStopoff={fieldsEditable ? (id, payload) => chargeOp(() => quoteService.updateStopoff(data.quoteNumber!, data.revisionNumber!, id, payload)) : undefined}
            onDeleteStopoff={fieldsEditable ? (id) => chargeOp(() => quoteService.deleteStopoff(data.quoteNumber!, data.revisionNumber!, id)) : undefined}
            chargeCatalog={deliveryChargeCatalog}
            onAddLaneCharge={fieldsEditable ? handleAddLaneCharge : undefined}
            onChangeCharge={fieldsEditable ? (id, patch) => chargeOp(() => quoteService.updateCharge(data.quoteNumber!, data.revisionNumber!, id, patch)) : undefined}
            onDeleteCharge={fieldsEditable ? (id) => chargeOp(() => quoteService.deleteCharge(data.quoteNumber!, data.revisionNumber!, id)) : undefined}
            rateStandards={rateStandards}
          />
        )}
        {tab === 'docs'  && <DocumentsTab quoteNumber={data.quoteNumber} revisionNumber={data.revisionNumber} />}
        {tab === 'notes' && (
          <NotesTab
            notes={data.notes ?? []}
            quoteNumber={data.quoteNumber}
            revisionNumber={data.revisionNumber}
            canAdd={mode === 'view' && Boolean(data.quoteNumber)}
            onAdded={() => { void reloadFromServer(); }}
          />
        )}
        {tab === 'hist'  && <HistoryTab quoteNumber={data.quoteNumber} />}
      </div>

      <div className="bg-panel border-t border-slate-200 dark:border-gray-700 px-[18px] py-3 flex items-center justify-between gap-2 flex-shrink-0 shadow-[0_-2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex gap-2 items-center text-[0.6875rem] text-slate-500 dark:text-slate-400">
          <span className="text-green-700">● {data.status}</span>
          {data.lastUsed && (
            <>
              <span className="text-slate-300">·</span>
              <span>Last used <span className="text-slate-900 font-semibold">{data.lastUsed}</span></span>
              <span className="text-slate-300">·</span>
              <span><span className="text-slate-900 font-semibold">{data.daysSince}</span> days ago</span>
            </>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {editable && isDirty && (
            <span className="text-[0.6875rem] font-semibold text-amber-700">Unsaved changes</span>
          )}
          <GhostBtn onClick={guardedGoBack}>Cancel</GhostBtn>
          {canDupe && <GhostBtn onClick={handleDupe}>Dupe</GhostBtn>}
          {canNewRevision && <GhostBtn onClick={handleNewRevision}>New Revision</GhostBtn>}
          {editable && (
            <>
              <GhostBtn onClick={revert} disabled={!isDirty || saving}>Revert</GhostBtn>
              <SaveBtn onClick={handleSave} disabled={!isDirty || saving || storageReasonMissing || saveBlockedByMin} label={saving ? 'Saving…' : undefined} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

/** Panel shown when the Direct tab needs its directquote row but it's missing.
 *  Unusual but possible on partially-created revisions in V1. */
const MissingDataPanel: React.FC<{ kind: 'WAREHOUSE' | 'DIRECT' }> = ({ kind }) => (
  <div className="bg-panel border border-slate-200 dark:border-gray-700 rounded-lg px-6 py-8 text-center text-slate-500 dark:text-slate-400 text-[0.78125rem]">
    No {kind.toLowerCase()} data on this revision yet. Save the quote with at least one rate
    row to populate this tab.
  </div>
);

const LoadingShell: React.FC<{ goBack: () => void; quoteIdLabel: string }> = ({ goBack, quoteIdLabel }) => (
  <div className="flex-1 flex flex-col">
    <div className="bg-panel px-[22px] pt-3.5 pb-2.5 flex items-center justify-between gap-4 flex-shrink-0">
      <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2.5">
        <span className="text-blue-600 font-semibold">Quote {quoteIdLabel}</span>
        <span className="text-[0.78125rem] text-slate-400 font-normal">loading…</span>
      </h1>
      <div className="flex gap-2">
        <GhostBtn onClick={goBack}>← Back to Quotes</GhostBtn>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400 text-[0.78125rem]" role="status">
      Loading quote…
    </div>
  </div>
);

const ErrorShell: React.FC<{ title: string; body: string; goBack: () => void }> = ({ title, body, goBack }) => (
  <div className="flex-1 flex flex-col">
    <div className="bg-panel px-[22px] pt-3.5 pb-2.5 flex items-center justify-between gap-4 flex-shrink-0">
      <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <div className="flex gap-2">
        <GhostBtn onClick={goBack}>← Back to Quotes</GhostBtn>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center">
      <div className="bg-panel border border-slate-200 dark:border-gray-700 rounded-lg px-6 py-8 max-w-md text-center">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">{title}</div>
        <div className="text-[0.78125rem] text-slate-600 dark:text-slate-400">{body}</div>
      </div>
    </div>
  </div>
);

const GhostBtn: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean }> = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const SaveBtn: React.FC<{ onClick?: () => void; disabled?: boolean; label?: string }> = ({ onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="px-5 py-2 text-[0.8125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded-md cursor-pointer inline-flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {label ? <span>{label}</span> : <span><u>S</u>ave</span>}
  </button>
);

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  // Unmapped status falls back to UNSECURED (red) here — see statusBadgeClass.
  <span className={`px-2.5 py-[3px] text-[0.6875rem] font-bold uppercase tracking-wider rounded border ${statusBadgeClass(status, 'UNSECURED')}`}>
    {status}
  </span>
);

const TypePill: React.FC<{ type: string }> = ({ type }) => (
  <span className="px-2.5 py-[3px] text-[0.6875rem] font-bold uppercase tracking-wider rounded bg-indigo-50 text-indigo-800 border border-indigo-200">
    {type} QUOTE
  </span>
);

export default QuoteDetailPage;
